/**
 * BriefGrid
 *
 * Grid view of all generated content briefs with filtering and bulk actions.
 */

import React, { useState, useMemo } from 'react';
import {
    Search,
    Filter,
    CheckCircle,
    Check,
    Image,
    Video,
    Layers,
    X
} from 'lucide-react';
import BriefCard from './BriefCard';

const BriefGrid = ({ briefs, onBriefClick, onBriefApprove, onBriefUnapprove }) => {
    // Filter states
    const [searchQuery, setSearchQuery] = useState('');
    const [funnelFilter, setFunnelFilter] = useState('all');
    const [statusFilter, setStatusFilter] = useState('all');
    const [pillarFilter, setPillarFilter] = useState('all');
    const [showFilters, setShowFilters] = useState(false);

    // Get unique pillars from briefs
    const uniquePillars = useMemo(() => {
        const pillars = new Map();
        briefs.forEach(brief => {
            if (brief.pillarId && brief.pillarName) {
                pillars.set(brief.pillarId, {
                    id: brief.pillarId,
                    name: brief.pillarName,
                    color: brief.pillarColor
                });
            }
        });
        return Array.from(pillars.values());
    }, [briefs]);

    // Filter briefs
    const filteredBriefs = useMemo(() => {
        return briefs.filter(brief => {
            const briefData = brief.brief || brief;

            // Search filter
            if (searchQuery) {
                const query = searchQuery.toLowerCase();
                const headline = (briefData.selectedHeadline || briefData.headline || '').toLowerCase();
                const angle = (briefData.marketingAngle || '').toLowerCase();
                const pillar = (brief.pillarName || '').toLowerCase();

                if (!headline.includes(query) && !angle.includes(query) && !pillar.includes(query)) {
                    return false;
                }
            }

            // Funnel stage filter
            if (funnelFilter !== 'all' && brief.funnelStage !== funnelFilter) {
                return false;
            }

            // Status filter
            if (statusFilter !== 'all' && brief.status !== statusFilter) {
                return false;
            }

            // Pillar filter
            if (pillarFilter !== 'all' && brief.pillarId !== pillarFilter) {
                return false;
            }

            return true;
        });
    }, [briefs, searchQuery, funnelFilter, statusFilter, pillarFilter]);

    // Group briefs by funnel stage for display
    const briefsByStage = useMemo(() => {
        return {
            TOFU: filteredBriefs.filter(b => b.funnelStage === 'TOFU'),
            MOFU: filteredBriefs.filter(b => b.funnelStage === 'MOFU'),
            BOFU: filteredBriefs.filter(b => b.funnelStage === 'BOFU')
        };
    }, [filteredBriefs]);

    // Stats
    const stats = useMemo(() => ({
        total: briefs.length,
        filtered: filteredBriefs.length,
        approved: briefs.filter(b => b.status === 'approved').length,
        byStage: {
            TOFU: briefs.filter(b => b.funnelStage === 'TOFU').length,
            MOFU: briefs.filter(b => b.funnelStage === 'MOFU').length,
            BOFU: briefs.filter(b => b.funnelStage === 'BOFU').length
        }
    }), [briefs, filteredBriefs]);

    // Bulk approve all visible
    const handleBulkApprove = () => {
        filteredBriefs
            .filter(b => b.status !== 'approved')
            .forEach(b => onBriefApprove(b.id));
    };

    const hasActiveFilters = funnelFilter !== 'all' || statusFilter !== 'all' || pillarFilter !== 'all' || searchQuery;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            {/* Stats Bar */}
            <div style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--space-4)',
                padding: 'var(--space-3) var(--space-4)',
                background: 'var(--color-bg-secondary)',
                borderRadius: 'var(--radius-lg)'
            }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
                    <span style={{ fontWeight: 600 }}>{stats.filtered}</span>
                    <span style={{ color: 'var(--color-text-secondary)', fontSize: 'var(--font-size-sm)' }}>
                        {stats.filtered !== stats.total ? `of ${stats.total}` : ''} briefs
                    </span>
                </div>

                <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--space-3)',
                    marginLeft: 'auto'
                }}>
                    <span style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 'var(--space-1)',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--success-600)'
                    }}>
                        <CheckCircle size={14} />
                        {stats.approved} approved
                    </span>

                    {filteredBriefs.some(b => b.status !== 'approved') && (
                        <button
                            onClick={handleBulkApprove}
                            className="btn btn-sm btn-ghost"
                            style={{ color: 'var(--success-600)' }}
                        >
                            <Check size={14} />
                            Approve All Visible
                        </button>
                    )}
                </div>
            </div>

            {/* Search and Filters */}
            <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
                {/* Search */}
                <div style={{
                    position: 'relative',
                    flex: '1 1 250px',
                    minWidth: '200px'
                }}>
                    <Search
                        size={16}
                        style={{
                            position: 'absolute',
                            left: 'var(--space-3)',
                            top: '50%',
                            transform: 'translateY(-50%)',
                            color: 'var(--color-text-tertiary)'
                        }}
                    />
                    <input
                        type="text"
                        placeholder="Search briefs..."
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        className="input"
                        style={{ paddingLeft: 'var(--space-8)', width: '100%' }}
                    />
                </div>

                {/* Filter Toggle */}
                <button
                    onClick={() => setShowFilters(!showFilters)}
                    className={`btn ${showFilters || hasActiveFilters ? 'btn-primary' : 'btn-ghost'}`}
                >
                    <Filter size={16} />
                    Filters
                    {hasActiveFilters && (
                        <span style={{
                            marginLeft: 'var(--space-1)',
                            padding: '0 6px',
                            fontSize: '10px',
                            background: 'white',
                            color: 'var(--color-primary)',
                            borderRadius: 'var(--radius-full)'
                        }}>
                            {[funnelFilter !== 'all', statusFilter !== 'all', pillarFilter !== 'all', searchQuery].filter(Boolean).length}
                        </span>
                    )}
                </button>
            </div>

            {/* Filter Options */}
            {showFilters && (
                <div style={{
                    display: 'flex',
                    gap: 'var(--space-4)',
                    flexWrap: 'wrap',
                    padding: 'var(--space-3)',
                    background: 'var(--color-bg-secondary)',
                    borderRadius: 'var(--radius-md)'
                }}>
                    {/* Funnel Stage Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            Funnel Stage
                        </label>
                        <select
                            value={funnelFilter}
                            onChange={(e) => setFunnelFilter(e.target.value)}
                            className="input"
                            style={{ minWidth: '140px' }}
                        >
                            <option value="all">All Stages</option>
                            <option value="TOFU">TOFU ({stats.byStage.TOFU})</option>
                            <option value="MOFU">MOFU ({stats.byStage.MOFU})</option>
                            <option value="BOFU">BOFU ({stats.byStage.BOFU})</option>
                        </select>
                    </div>

                    {/* Status Filter */}
                    <div>
                        <label style={{
                            display: 'block',
                            fontSize: 'var(--font-size-xs)',
                            color: 'var(--color-text-secondary)',
                            marginBottom: 'var(--space-1)'
                        }}>
                            Status
                        </label>
                        <select
                            value={statusFilter}
                            onChange={(e) => setStatusFilter(e.target.value)}
                            className="input"
                            style={{ minWidth: '140px' }}
                        >
                            <option value="all">All Statuses</option>
                            <option value="ai_generated">AI Generated</option>
                            <option value="refined">Refined</option>
                            <option value="approved">Approved</option>
                        </select>
                    </div>

                    {/* Pillar Filter */}
                    {uniquePillars.length > 0 && (
                        <div>
                            <label style={{
                                display: 'block',
                                fontSize: 'var(--font-size-xs)',
                                color: 'var(--color-text-secondary)',
                                marginBottom: 'var(--space-1)'
                            }}>
                                Pillar
                            </label>
                            <select
                                value={pillarFilter}
                                onChange={(e) => setPillarFilter(e.target.value)}
                                className="input"
                                style={{ minWidth: '140px' }}
                            >
                                <option value="all">All Pillars</option>
                                {uniquePillars.map(pillar => (
                                    <option key={pillar.id} value={pillar.id}>
                                        {pillar.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                    )}

                    {/* Clear Filters */}
                    {hasActiveFilters && (
                        <button
                            onClick={() => {
                                setSearchQuery('');
                                setFunnelFilter('all');
                                setStatusFilter('all');
                                setPillarFilter('all');
                            }}
                            className="btn btn-ghost btn-sm"
                            style={{ alignSelf: 'flex-end' }}
                        >
                            <X size={14} />
                            Clear
                        </button>
                    )}
                </div>
            )}

            {/* Briefs Grid - Grouped by Stage */}
            {['TOFU', 'MOFU', 'BOFU'].map(stage => {
                const stageBriefs = briefsByStage[stage];
                if (stageBriefs.length === 0 && funnelFilter !== 'all' && funnelFilter !== stage) {
                    return null;
                }

                const stageColors = {
                    TOFU: { bg: 'var(--info-50)', text: 'var(--info-700)' },
                    MOFU: { bg: 'var(--warning-50)', text: 'var(--warning-700)' },
                    BOFU: { bg: 'var(--success-50)', text: 'var(--success-700)' }
                };

                const stageLabels = {
                    TOFU: 'Awareness',
                    MOFU: 'Consideration',
                    BOFU: 'Conversion'
                };

                return (
                    <div key={stage} style={{ marginBottom: 'var(--space-4)' }}>
                        {/* Stage Header */}
                        <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 'var(--space-2)',
                            marginBottom: 'var(--space-3)'
                        }}>
                            <span style={{
                                padding: '4px 12px',
                                fontSize: 'var(--font-size-sm)',
                                fontWeight: 600,
                                background: stageColors[stage].bg,
                                color: stageColors[stage].text,
                                borderRadius: 'var(--radius-md)'
                            }}>
                                {stage}
                            </span>
                            <span style={{
                                fontSize: 'var(--font-size-sm)',
                                color: 'var(--color-text-secondary)'
                            }}>
                                {stageLabels[stage]} · {stageBriefs.length} briefs
                            </span>
                        </div>

                        {/* Briefs Grid */}
                        {stageBriefs.length > 0 ? (
                            <div style={{
                                display: 'grid',
                                gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
                                gap: 'var(--space-3)'
                            }}>
                                {stageBriefs.map(brief => (
                                    <BriefCard
                                        key={brief.id}
                                        brief={brief}
                                        onClick={onBriefClick}
                                        onApprove={onBriefApprove}
                                        onUnapprove={onBriefUnapprove}
                                    />
                                ))}
                            </div>
                        ) : (
                            <div style={{
                                padding: 'var(--space-6)',
                                textAlign: 'center',
                                background: 'var(--color-bg-secondary)',
                                borderRadius: 'var(--radius-md)',
                                color: 'var(--color-text-tertiary)'
                            }}>
                                No {stage} briefs{hasActiveFilters ? ' matching filters' : ''}
                            </div>
                        )}
                    </div>
                );
            })}

            {/* Empty State */}
            {filteredBriefs.length === 0 && (
                <div style={{
                    padding: 'var(--space-12)',
                    textAlign: 'center',
                    color: 'var(--color-text-secondary)'
                }}>
                    {hasActiveFilters ? (
                        <>
                            <p>No briefs match your filters</p>
                            <button
                                onClick={() => {
                                    setSearchQuery('');
                                    setFunnelFilter('all');
                                    setStatusFilter('all');
                                    setPillarFilter('all');
                                }}
                                className="btn btn-ghost"
                                style={{ marginTop: 'var(--space-2)' }}
                            >
                                Clear Filters
                            </button>
                        </>
                    ) : (
                        <p>No briefs generated yet</p>
                    )}
                </div>
            )}
        </div>
    );
};

export default BriefGrid;
